import { z } from 'zod';
const text = (max) => z.string().trim().min(1).max(max);
const optionalText = (max) => z.string().trim().max(max).nullish();
export const calendarDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/).refine((s) => {
    const d = new Date(`${s}T00:00:00Z`);
    return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === s;
}, 'Choose a real calendar date.');
export const manualRecordSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('company'), key: z.string().uuid(), name: text(300),
        domain: optionalText(500), website: optionalText(500), orgNumber: optionalText(40),
        allowSeparate: z.boolean().default(false),
    }),
    z.object({
        kind: z.literal('contact'), key: z.string().uuid(), name: text(300),
        email: z.union([z.string().trim().email().max(320), z.literal('')]).nullish(),
        phone: z.string().trim().max(40).refine((s) => !s || /^\+?[0-9][0-9 ()./-]{4,39}$/.test(s), 'Use a valid phone number.').nullish(),
        employment: z.object({ companyId: z.string().uuid(), from: calendarDate, role: optionalText(200) }).nullish(),
        allowSeparate: z.boolean().default(false),
    }),
]);
export const manualCommunicationSchema = z.object({
    key: z.string().uuid(),
    companyId: z.string().uuid(),
    contactId: z.string().uuid().nullish(),
    taskId: z.string().uuid().nullish(),
    kind: z.enum(['call', 'meeting', 'sms', 'whatsapp', 'linkedin', 'email', 'note']),
    direction: z.enum(['inbound', 'outbound', 'internal']),
    occurredAt: z.string().datetime({ offset: true }),
    participants: z.array(text(320)).max(50).default([]),
    summary: text(8000),
    outcome: optionalText(2000),
    nextAction: optionalText(2000),
    provider: z.enum(['gmail', 'google_chat', 'whatsapp', 'sms', 'linkedin', 'phone']).nullish(),
    providerEventId: optionalText(500),
    createFollowUpTask: z.boolean().default(false),
}).superRefine((value, ctx) => {
    if ((value.provider == null) !== (value.providerEventId == null)) {
        ctx.addIssue({ code: 'custom', path: ['providerEventId'], message: 'Choose both a provider and its event id, or neither.' });
    }
});
export const communicationTaskSchema = z.object({
    key: z.string().uuid(),
    companyId: z.string().uuid(),
    contactId: z.string().uuid().nullish(),
    threadId: z.string().uuid().nullish(),
    manualCommunicationId: z.string().uuid().nullish(),
    objective: z.enum(['send_reply', 'resolve_enquiry', 'follow_up', 'attend', 'wait_reply']),
    title: text(200),
    reviewAt: z.string().datetime({ offset: true }).nullish(),
    prepareDraft: z.boolean().default(false),
}).superRefine((value, ctx) => {
    if (['send_reply', 'resolve_enquiry', 'wait_reply'].includes(value.objective) && !value.threadId) {
        ctx.addIssue({ code: 'custom', path: ['threadId'], message: 'A reply task names its conversation.' });
    }
    if (value.objective === 'wait_reply' && !value.reviewAt) {
        ctx.addIssue({ code: 'custom', path: ['reviewAt'], message: 'Waiting for a reply needs a review date.' });
    }
    if (value.prepareDraft && value.objective !== 'send_reply' && value.objective !== 'resolve_enquiry') {
        ctx.addIssue({ code: 'custom', path: ['prepareDraft'], message: 'Only a reply objective prepares a draft.' });
    }
});
export const communicationImportSchema = z.object({
    connectionId: z.string().uuid(),
    from: z.string().datetime({ offset: true }),
    to: z.string().datetime({ offset: true }).nullish(),
}).superRefine((value, ctx) => {
    if (value.to && new Date(value.to) < new Date(value.from)) {
        ctx.addIssue({ code: 'custom', path: ['to'], message: 'The import end cannot precede its start.' });
    }
});
export const communicationOutcomeSchema = z.object({
    key: z.string().uuid(),
    communicationTaskId: z.string().uuid(),
    manualCommunicationId: z.string().uuid(),
    outcome: z.enum(['sent', 'reply_received', 'call_connected', 'no_answer', 'voicemail', 'meeting_held', 'follow_up_done', 'resolved', 'needs_follow_up']),
    note: optionalText(2000),
});
export const strangerTriagePolicySchema = z.object({
    assignmentId: z.string().uuid(),
    addressedTo: z.string().uuid().nullish(),
    endsAt: z.string().datetime({ offset: true }),
    maxSearches: z.number().int().min(1).max(5),
    maxPages: z.number().int().min(0).max(10),
    maxCostMicros: z.number().int().min(1).max(100_000_000),
});
export const strangerResearchQueueSchema = z.object({
    unresolvedId: z.string().uuid(),
});
export const strangerPriorityOverrideSchema = z.object({
    researchId: z.string().uuid(),
    priority: z.enum(['high', 'normal', 'when_time_allows', 'needs_review']),
    reason: text(500),
});
export const companyRelationshipRoleSchema = z.object({
    companyId: z.string().uuid(), role: z.enum(['prospect', 'customer', 'supplier', 'partner', 'investor']),
    state: z.enum(['active', 'paused', 'completed']).default('active'), validFrom: z.string().date(), validTo: z.string().date().nullish(),
    ownerHuman: z.string().uuid().nullish(), ownerAgent: z.string().uuid().nullish(), nextActionTask: z.string().uuid().nullish(), blockerTask: z.string().uuid().nullish(), source: text(200),
}).refine(v => !(v.ownerHuman && v.ownerAgent), { path: ['ownerAgent'], message: 'Choose one owner.' });
export const companyGroupLinkSchema = z.object({
    companyId: z.string().uuid(), relatedCompanyId: z.string().uuid(), relation: z.enum(['parent', 'subsidiary', 'brand', 'trading_name']),
    validFrom: z.string().date(), validTo: z.string().date().nullish(), sourceUrl: z.string().url().startsWith('https://').nullish(),
}).refine(v => v.companyId !== v.relatedCompanyId, { path: ['relatedCompanyId'], message: 'A company cannot be related to itself.' });
export const companyEnrichmentProposalSchema = z.object({
    key: z.string().uuid(), companyId: z.string().uuid(), fieldKey: z.string().regex(/^[a-z][a-z0-9_]{0,60}$/),
    currentValue: z.unknown().nullish(), proposedValue: z.unknown(), sources: z.array(z.object({ url: z.string().url().startsWith('https://'), observedAt: z.string().datetime({ offset: true }) }).passthrough()).min(1).max(20),
    confidence: z.number().min(0).max(1), identityCertain: z.boolean(), conflict: z.boolean(), researchTaskId: z.string().uuid().nullish(),
});
export const companyEnrichmentDecisionSchema = z.object({ proposalId: z.string().uuid(), decision: z.enum(['accepted', 'rejected']), reason: text(1000) });
export const sourceRefreshSchema = z.object({ key: z.string().uuid(), freshnessId: z.string().uuid() });
export const gcpCostSnapshotSchema = z.object({
    periodStart: z.string().date(), periodEnd: z.string().date(), modelCostUsd: z.number().nonnegative(), searchCostUsd: z.number().nonnegative(), budgetUsd: z.number().nonnegative().nullish(),
    creditState: z.enum(['verified', 'unknown', 'not_eligible', 'exhausted']), creditBalanceUsd: z.number().nonnegative().nullish(), verifiedAt: z.string().datetime({ offset: true }).nullish(), sourceRef: z.string().max(500).nullish(),
}).superRefine((v, c) => { if (v.creditState === 'verified' && (!v.verifiedAt || !v.sourceRef || v.creditBalanceUsd == null))
    c.addIssue({ code: 'custom', path: ['creditState'], message: 'Verified credit needs its balance, time and source.' }); });
export const companyBackfillSchema = z.object({ runId: z.string().uuid().nullish(), kind: z.enum(['source_freshness', 'enrichment_inventory', 'relationship_inventory']), dryRun: z.boolean(), limit: z.number().int().min(1).max(200), cursor: z.string().uuid().nullish() });
export function companyDomain(raw) {
    if (!raw?.trim())
        return null;
    try {
        const u = new URL(/^https?:\/\//i.test(raw) ? raw : `https://${raw}`);
        if (!['https:', 'http:'].includes(u.protocol) || u.username || u.password || !u.hostname.includes('.'))
            return null;
        return u.hostname.toLowerCase().replace(/^www\./, '').replace(/\.$/, '');
    }
    catch {
        return null;
    }
}
/** Only these source-bound operations can run inside a company panel.
 * Each still runs its original handler, including edit rights and tool gates. */
const RECORD_OPERATIONS = {
    task: Object.fromEntries(['get', 'close', 'update', 'assign', 'remove', 'approve-held', 'notes/list', 'notes/add', 'elaborate', 'schedule', 'standing'].map(op => [`/v1/tasks/${op}`, 'taskId']).concat([['/v1/tasks/assignees', null]])),
    project: Object.fromEntries(['get', 'room', 'room/say', 'meetings/convene', 'add-member', 'draft'].map(op => [`/v1/projects/${op}`, 'projectId'])),
    request: Object.fromEntries(['read', 'standing', 'complete', 'refuse', 'cancel', 'acknowledge'].map(op => [`/v1/requests/${op}`, 'requestId'])),
    supplier: { ...Object.fromEntries(['get', 'answer', 'ask', 'asked', 'contact', 'find-contact', 'samples'].map(op => [`/v1/suppliers/${op}`, 'supplierId'])), '/v1/suppliers/confirm': null },
    prospect: Object.fromEntries(['details', 'objection', 'intro', 'sample', 'opt-out', 'erase', 'lost'].map(op => [`/v1/prospects/${op}`, 'prospectId']).concat(['', '/exclude', '/retry', '/draft'].map(op => [`/v1/outreach/prospect${op}`, 'prospectId']))),
    thread: { '/v1/threads/read': 'threadId', '/v1/threads/claim': 'threadId', '/v1/threads/unclaim': 'threadId', '/v1/threads/remove': 'threadId', '/v1/drafts/compose': 'threadId', '/v1/merges/list': 'threadId' },
    offer: { ...Object.fromEntries(['get', 'prepare', 'sent', 'send-to'].map(op => [`/v1/offers/${op}`, 'offerId'])), '/v1/offers/recipient-sent': null },
    buyer: { '/v1/buyers/list': null, '/v1/buyers/approve': 'buyerId' },
    signal: { '/v1/signals/list': null },
    campaign: { '/v1/outreach/campaign': 'campaignId', '/v1/outreach/campaigns/end': 'campaignId' },
};
export function companyRecordOperation(kind, path) {
    const operations = RECORD_OPERATIONS[kind];
    return operations && Object.hasOwn(operations, path) ? { field: operations[path] } : null;
}
//# sourceMappingURL=company-workspace.js.map